import React, { useRef, useEffect, useCallback } from "react";
import { useWhatsNew, FeatureUpdate } from "../../../contexts/WhatsNewContext";

// ============ HELPER FUNCTIONS ============

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Future date (upcoming)
  if (diffMs < 0) {
    const futureDays = Math.abs(diffDays);
    if (futureDays === 0) return "Coming today";
    if (futureDays === 1) return "Coming tomorrow";
    if (futureDays < 7) return `Coming in ${futureDays} days`;
    if (futureDays < 30) return `Coming in ${Math.ceil(futureDays / 7)} weeks`;
    return date.toLocaleDateString("en-IN", { month: "short", day: "numeric" });
  }

  // Past date
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  
  return date.toLocaleDateString("en-IN", { month: "short", day: "numeric" });
};

const getTypeBadge = (type: FeatureUpdate["type"]): { label: string; className: string } => {
  switch (type) {
    case "new":
      return {
        label: "NEW",
        className: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400",
      };
    case "improvement":
      return {
        label: "IMPROVED",
        className: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400",
      };
    case "upcoming":
      return {
        label: "COMING SOON",
        className: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400",
      };
    case "beta":
      return {
        label: "BETA",
        className: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400",
      };
    case "fix":
      return {
        label: "FIX",
        className: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
      };
    default:
      return {
        label: "UPDATE",
        className: "bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-400",
      };
  }
};

const getTypeIcon = (type: FeatureUpdate["type"]): React.ReactNode => {
  const iconClass = "w-5 h-5";

  switch (type) {
    case "new":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      );
    case "improvement":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      );
    case "upcoming":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case "beta":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      );
    case "fix":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    default:
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
  }
};

const getTypeIconColor = (type: FeatureUpdate["type"]): string => {
  switch (type) {
    case "new":
      return "text-green-500 bg-green-100 dark:bg-green-900/30";
    case "improvement":
      return "text-blue-500 bg-blue-100 dark:bg-blue-900/30";
    case "upcoming":
      return "text-purple-500 bg-purple-100 dark:bg-purple-900/30";
    case "beta":
      return "text-amber-500 bg-amber-100 dark:bg-amber-900/30";
    case "fix":
      return "text-gray-500 bg-gray-100 dark:bg-gray-700";
    default:
      return "text-primary-500 bg-primary-100 dark:bg-primary-900/30";
  }
};

// ============ UPDATE ITEM COMPONENT ============

interface UpdateItemProps {
  update: FeatureUpdate;
  onRead: (id: string) => void;
  onNavigate: (link: string, isExternal?: boolean) => void;
}

const UpdateItem: React.FC<UpdateItemProps> = ({ update, onRead, onNavigate }) => {
  const handleClick = () => {
    if (!update.isRead) {
      onRead(update.id);
    }
    if (update.link) {
      onNavigate(update.link, update.isExternal);
    }
  };

  const badge = getTypeBadge(update.type);

  return (
    <div
      className={`group relative p-4 cursor-pointer transition-colors duration-150 ${
        update.isRead
          ? "bg-transparent hover:bg-gray-50 dark:hover:bg-gray-700/50"
          : "bg-primary-50/30 dark:bg-primary-900/10 hover:bg-primary-50/50 dark:hover:bg-primary-900/20"
      }`}
      onClick={handleClick}
    >
      {/* Unread indicator */}
      {!update.isRead && (
        <div className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-primary-500" />
      )}

      <div className="flex gap-3">
        {/* Icon */}
        <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${getTypeIconColor(update.type)}`}>
          {getTypeIcon(update.type)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header with badge and date */}
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold rounded ${badge.className}`}>
              {badge.label}
            </span>
            {update.version && (
              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                v{update.version}
              </span>
            )}
            <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-auto">
              {formatDate(update.date)}
            </span>
          </div>

          {/* Title */}
          <h4 className={`text-sm font-semibold mb-1 ${update.isRead ? "text-gray-700 dark:text-gray-300" : "text-gray-900 dark:text-gray-100"}`}>
            {update.title}
          </h4>

          {/* Description */}
          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
            {update.description}
          </p>

          {/* Tags */}
          {update.tags && update.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {update.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Action hint */}
          {update.link && (
            <span className="inline-flex items-center gap-1 text-[11px] text-primary-600 dark:text-primary-400 mt-2 font-medium">
              {update.type === "upcoming" ? "Learn more" : "Try it now"}
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ MAIN COMPONENT ============

interface WhatsNewDropdownProps {
  onNavigate?: (screenId: string) => void;
}

export const WhatsNewDropdown: React.FC<WhatsNewDropdownProps> = ({ onNavigate }) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const {
    updates,
    unreadCount,
    isLoading,
    isOpen,
    toggleOpen,
    setOpen,
    markAsRead,
    markAllAsRead,
    setNavigationCallback,
  } = useWhatsNew();

  // Register navigation callback
  useEffect(() => {
    if (onNavigate) {
      setNavigationCallback(onNavigate);
    }
  }, [onNavigate, setNavigationCallback]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, setOpen]);

  // Handle navigation
  const handleNavigate = useCallback((link: string, isExternal?: boolean) => {
    if (isExternal) {
      window.open(link, "_blank", "noopener,noreferrer");
    } else if (link === "start-tour") {
      window.dispatchEvent(new CustomEvent("start-app-tour"));
    } else if (onNavigate) {
      onNavigate(link);
    }
    setOpen(false);
  }, [onNavigate, setOpen]);

  return (
    <div ref={dropdownRef} className="relative" data-tour="whats-new">
      {/* Sparkle/Gift Icon Button */}
      <button
        onClick={toggleOpen}
        className="relative p-2 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none"
        aria-label={`What's New${unreadCount > 0 ? ` (${unreadCount} updates)` : ""}`}
        title="What's New"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
        </svg>

        {/* Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-gradient-to-r from-purple-500 to-pink-500 rounded-full shadow-sm">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-[1001]">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xl">🎁</span>
                <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">
                  What's New
                </h3>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 text-[10px] font-bold text-white bg-gradient-to-r from-purple-500 to-pink-500 rounded-full">
                    {unreadCount} new
                  </span>
                )}
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors"
                >
                  Mark all read
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Latest features, updates & upcoming releases
            </p>
          </div>

          {/* Updates List */}
          <div className="max-h-[400px] overflow-y-auto custom-scrollbar divide-y divide-gray-100 dark:divide-gray-700/50">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500" />
              </div>
            ) : updates.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
                  <span className="text-2xl">📦</span>
                </div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  No updates yet
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Check back soon for new features!
                </p>
              </div>
            ) : (
              updates.map((update) => (
                <UpdateItem
                  key={update.id}
                  update={update}
                  onRead={markAsRead}
                  onNavigate={handleNavigate}
                />
              ))
            )}
          </div>

          {/* Footer */}
          {updates.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-2 bg-gray-50 dark:bg-gray-750">
              <button
                onClick={() => handleNavigate("changelog")}
                className="w-full text-center text-xs font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 py-1 transition-colors flex items-center justify-center gap-1"
              >
                View all updates
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
